import {
	Box,
	Button,
	Divider,
	Unstable_Grid2 as Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton
} from "@mui/material";
import { useFields } from "../../../../api/useFields";
import { displayEstimatedSessions, getColumnsNames } from "../utils";
import { Avatar } from "../../../../components/fieldsViews/fields/Avatar";
import { GroupsTitle } from "../../../../components/groups/GroupsTitle";
import { FieldName } from "../../../../components/groups/FieldName";
import { useStore } from "../../../../store/store";
import { AppDialog } from "../../../../store/store.types";
import { ServicesPrescribedDialog } from "../treatment-prescription-input/ServicesPrescribedDialog";
import {
	Edit as EditIcon,
	AddCircle as AddCircleIcon,
	VisibilityOff as VisibilityOffIcon,
	Visibility as VisibilityIcon
} from "@mui/icons-material";
import { useContext, useState } from "react";
import { RecordContext } from "../../../../components/record/Record";
import { TreatmentPrescription } from "../types";

type ServicesPrescribedViewProps = {
	treatmentPrescription: TreatmentPrescription;
};
export const ServicesPrescribedView = ({ treatmentPrescription }: ServicesPrescribedViewProps) => {
	const servicesPrescribed = treatmentPrescription?.services_prescribed;
	const { formatedFields: formatedFieldsServicesPrescribed } = useFields("services_prescribed");
	const { appDialog, hideUIFromClient } = useStore();
	const treatment = useContext(RecordContext);
	const [visibleGoalsIds, setVisibleGoalsIds] = useState<Array<number>>([]);

	if (!treatment?.id) return null;

	const handleEditServicesPrescription = (appDialog: AppDialog) => {
		appDialog.createDialog({
			title: "Prescrição de Serviços",
			allowSoftCancel: true,
			maxWidth: "xl",
			contentAndActions: (
				<ServicesPrescribedDialog
					treatmentId={Number(treatment.id)}
					treatmentPrescription={treatmentPrescription}
				/>
			)
		});
	};

	const toggleGoalsVisibility = (serviceId: number) => {
		const isVisible = visibleGoalsIds.includes(serviceId);
		if (isVisible) {
			const updatedVisibleIds = visibleGoalsIds.filter((id) => id !== serviceId);
			setVisibleGoalsIds(updatedVisibleIds);
		} else {
			const updatedVisibleIds = [...visibleGoalsIds, serviceId];
			setVisibleGoalsIds(updatedVisibleIds);
		}
	};

	const columnNames = getColumnsNames(formatedFieldsServicesPrescribed);

	return (
		<Grid container spacing={4} p={4}>
			<Grid xs={12}>
				<GroupsTitle title="Serviços Prescritos" />
				<PrescriberTechnician treatmentPrescription={treatmentPrescription} />
			</Grid>

			<Grid xs={12} md={6}>
				<TableContainer component={Paper} sx={{ mb: 2 }}>
					<Table
						size="small"
						aria-label="table"
						sx={{
							"& td, & th": {
								fontSize: "1rem"
							},
							"& th": { color: "primary.main" }
						}}
					>
						<TableHead>
							<TableRow sx={{ fontWeight: "bold" }}>
								{columnNames.map((column) => (
									<TableCell
										align={column === "Serviço" ? "left" : "center"}
										key={column}
										sx={{ width: column === "goals" || column === "Objetivos" ? "40%" : "auto" }}
									>
										{column}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{servicesPrescribed?.map((servicePrescribed) => {
								const hasGoals = servicePrescribed.goals && servicePrescribed.goals.length > 0;
								const isGoalsVisible = visibleGoalsIds.includes(servicePrescribed.id);

								return (
									<TableRow
										key={servicePrescribed.id}
										sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
									>
										<TableCell>{servicePrescribed.service.name}</TableCell>
										<TableCell align="center">
											{displayEstimatedSessions(
												servicePrescribed.estimated_total_sessions,
												servicePrescribed.service.id
											)}
										</TableCell>
										<TableCell sx={{ width: "40%" }}>
											{hasGoals ? (
												<Box>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															mb: isGoalsVisible ? 1 : 0
														}}
													>
														<IconButton
															size="small"
															onClick={() => toggleGoalsVisibility(servicePrescribed.id)}
															sx={{ color: "primary.main" }}
														>
															{isGoalsVisible ? (
																<VisibilityIcon fontSize="small" />
															) : (
																<VisibilityOffIcon fontSize="small" />
															)}
														</IconButton>
													</Box>
													{isGoalsVisible && (
														<Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
															{servicePrescribed.goals?.map((goal, index) => (
																<Typography key={index} component="li" variant="body1">
																	{goal.treatment_therapy_goals_id?.goal}
																</Typography>
															))}
														</Box>
													)}
												</Box>
											) : (
												<Box sx={{ display: "flex", justifyContent: "center" }}>
													<Typography variant="body2" color="text.secondary">
														Não definido
													</Typography>
												</Box>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>

			<Grid xs={12} md={6}>
				<Paper
					sx={{ px: 0, py: 1, display: hideUIFromClient.isHiddenFromClient ? "none" : "block" }}
					elevation={2}
				>
					<FieldName sx={{ px: 4 }} headerName="Informações Adicionais" />
					{treatmentPrescription?.content && (
						<Divider sx={{ my: 1 }} variant="fullWidth" orientation="horizontal" />
					)}
					<Box sx={{ px: 4 }} dangerouslySetInnerHTML={{ __html: treatmentPrescription?.content }} />
				</Paper>
			</Grid>

			<Grid xs={12} sx={{ display: "flex", justifyContent: "flex-start" }}>
				<Button
					fullWidth
					sx={{ mt: treatmentPrescription ? 2 : 4, maxWidth: "400px" }}
					onClick={() => handleEditServicesPrescription(appDialog)}
					startIcon={treatmentPrescription ? <EditIcon /> : <AddCircleIcon />}
				>
					{treatmentPrescription ? "Editar" : "Criar"}
				</Button>
			</Grid>
		</Grid>
	);
};

const PrescriberTechnician = ({ treatmentPrescription }: { treatmentPrescription: TreatmentPrescription }) => {
	if (!treatmentPrescription) return null;
	return (
		<Box display="flex" flexDirection="column" mb={6}>
			<Box display="flex" flexDirection="row" alignItems="center">
				<Avatar value={treatmentPrescription?.user_created?.avatar?.id} />
				<Typography variant="body1" ml={2}>
					{treatmentPrescription?.user_created?.first_name +
						" " +
						treatmentPrescription?.user_created?.last_name}
				</Typography>
			</Box>
		</Box>
	);
};
